import * as React from "react";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { addDays } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { ClientDetails } from "../../../../../../../Wrap/Models/Models/Processes/Quote/ClientDetails";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { isTaxRelifAvailable } from "../../../NewQuoteWizard/Components/Shared/Utils";
import {
    datePickerOptions,
    FrequencyArraysMap,
    FrequencyTypeArrays,
    PaymentType,
    processTypeEnum,
} from "../../Resource/Contents";
import { Indexation } from "./Indexation";
import { ThirdPartyEmployerDetail } from "../SinglePayment/ThirdPartyEmployerDetail";
import { RegularPaymentMethods } from "./RegularPaymentMethods";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import {
    PaymentInMessage,
    SourceOfFundsArray,
    SourceOfFundsArrayMap,
} from "../../../NewBusinessWizard/Resource/Contents";
import { CountryCodeMap } from "../../../../../../Shared/Entities/Maps/CountryCodeMap";
import { CountryCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CountryCode";
import { RegularDepositMethod } from "../../../../../../../Wrap/Constants/Enums/Domain/RegularDepositMethod";

interface IRegularPaymentDetailProps extends IProcessComponentProps {
    clientInfo: ClientDetails;
    label: string;
    subAccountType: SubAccountType;
    type: PaymentType;
    hasPaymentMethod: boolean;
    bankAccounts?: BankAccount[];
    bankAccountInstructions?: BankAccountInstruction[];
    processType: processTypeEnum;
    onSaveNewBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    onRemoveBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    inUseBankAccountInstruction?: string[];
    autoLineCompleted?: boolean;
}

interface IRegularPaymentDetailState {
    continueUntilDate: boolean;
    taxReliefStatus: boolean | null;
    isTaxReliefApplicable: boolean;
}

export class RegularPaymentDetail extends ProcessComponent<IRegularPaymentDetailProps, IRegularPaymentDetailState> {
    public static defaultProps = {
        autoLineCompleted: true,
    };
    constructor(props: any) {
        super(props);
        this.state = {
            continueUntilDate: props.data.ContinueUntil != null,
            taxReliefStatus: null,
            isTaxReliefApplicable: true,
        };
    }

    async componentDidMount(): Promise<void> {
        super.componentDidMount();
        const { data } = this.props;
        if (data?.RegularBuyId && !data?.NextPaymentDate) {
            this.props.onChange({ NextPaymentDate: data?.ContinueUntil });
        }        

        const productAvailableForTaxRelief = this.props.subAccountType === SubAccountType.Npr || this.props.subAccountType === SubAccountType.JuniorSipp;

        const firstCollectionDate = data?.FirstCollectionDate;
        if (firstCollectionDate != null) {
            const taxReliefAvailable = await isTaxRelifAvailable(
                this.props.clientInfo?.DateOfBirth,
                firstCollectionDate
            ) && productAvailableForTaxRelief;

            this.setState({
                taxReliefStatus: taxReliefAvailable,
                isTaxReliefApplicable: taxReliefAvailable,
            });
        }

        if (data?.TaxRelief === null && productAvailableForTaxRelief) {
            this.props.onChange({ TaxRelief: true });
        } else if (
            typeof data?.TaxRelief === "object" &&
            Object.keys(data?.TaxRelief).length === 0 &&
            this.state.isTaxReliefApplicable &&
            productAvailableForTaxRelief
        ) {
            this.props.onChange({ TaxRelief: true });
        }
    }

    private onEdit(changes) {
        this.props.onChange({
            ...changes,
            WasPaymentChanged: true,
        });
    }

    private async handleDateChange(v: Date) {
        // Check for tax relief availability
        const taxReliefAvailable = await isTaxRelifAvailable(
            this.props.clientInfo?.DateOfBirth,
            JsonDateParser.toJsonDateNoOffset(v)
        ) && (this.props.subAccountType === SubAccountType.Npr || this.props.subAccountType === SubAccountType.JuniorSipp);

        // Update the state with tax relief status and other properties
        this.setState(
            {
                taxReliefStatus: taxReliefAvailable,
                isTaxReliefApplicable: taxReliefAvailable, // updating the new state property
            },
            () => {
                this.onEdit({
                    NextPaymentDate: this.props.data?.RegularBuyId ? JsonDateParser.toJsonDateNoOffset(v) : null,
                    FirstCollectionDate: JsonDateParser.toJsonDateNoOffset(v),
                    TaxRelief: SubAccountTypeHelper.isOnPlatformPension(this.props.subAccountType)
                        ? this.state.taxReliefStatus
                        : null,
                });
            }
        );
    }

    public render(): JSX.Element {
        const bankAccounts = this.props.bankAccounts;
        const bankAccountInstructions = this.props.bankAccountInstructions;
        const {
            data,
            onChange,
            type,
            processType,
            onSaveNewBankAccountInstruction,
            onRemoveBankAccountInstruction,
            inUseBankAccountInstruction,
            clientInfo,
        } = this.props;
        const getFirstPaymentDate = (readonly: boolean) => {
            const curDate = new Date();
            const daysToAdd =
                curDate.getDay() == 0
                    ? 12 //sunday = 0
                    : curDate.getDay() == 6
                    ? 13 //saturday = 6
                    : 14;
            return readonly ? curDate : addDays(curDate, daysToAdd);
        };
        const isHypotheticalProcessTypes = this.props.processType == processTypeEnum.Quote || this.props.processType == processTypeEnum.WhatIfTool;
        return (
            <>
                <h4>{type}</h4>
                <div className="row">
                    <div className="col-md-4 col-12 pb-20">
                        <MoneyTextBoxEditor
                            required={"Please enter regular payment"}
                            value={data?.Money?.Amount || 0}
                            currency={data?.Money?.Currency || CurrencyCode.GBP}
                            label={
                                data?.TaxRelief && SubAccountTypeHelper.isOnPlatformPension(this.props.subAccountType)
                                    ? "Regular payment (net)"
                                    : "Regular payment"
                            }
                            validationRules={[
                                {
                                    errorMessage: isHypotheticalProcessTypes ? "Regular payment value must be >= 0" : "Regular payment value must be > 0",
                                    validator: (v) => (isHypotheticalProcessTypes ? v >= 0 : v > 0),
                                },
                            ]}
                            onChange={(v) => this.onEdit({ Money: { Amount: v, Currency: CurrencyCode.GBP } })}
                        />
                    </div>
                    <div className="col-md-4 col-12">
                        <SelectEditor
                            options={FrequencyTypeArrays}
                            getText={(v) => {
                                return FrequencyArraysMap[v];
                            }}
                            required="Please select payment frequency"
                            placeholder="Please select"
                            selected={data?.Frequency}
                            label="Payment frequency"
                            onChange={(v) => this.onEdit({ Frequency: v })}
                        />
                    </div>
                </div>
                {processType != processTypeEnum.Quote && processType != processTypeEnum.WhatIfTool && (
                    <div className="row">
                        <div className="col-md-4 col-12 pb-20">
                            <SelectEditor
                                options={SourceOfFundsArray}
                                getText={(v) => SourceOfFundsArrayMap[v]}
                                required="Please select"
                                label="Source of funds"
                                placeholder="Please select"
                                selected={data?.SourceOfFunds}
                                onChange={(v) => {
                                    this.onEdit({ SourceOfFunds: v });
                                }}
                                tooltip={PaymentInMessage.sourceOfFundsToolTip}
                            />
                        </div>
                        <div className="col-md-4 col-12">
                            <SelectEditor
                                options={CountryCodeMap.getArray(CountryCode.GBR)}
                                getText={(v) => {
                                    return CountryCodeMap.getString(v);
                                }}
                                required={"Please select"}
                                label="Country of origin"
                                selected={data?.OriginCountry}
                                placeholder={"Please select"}
                                onChange={(v) => {
                                    this.onEdit({ OriginCountry: v });
                                }}
                            />
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-md-4">
                        <DatePicker
                            width="100%"
                            filterDate={{
                                errorMessage: "Date must also be between 1-28 of any month",
                                validator: (v) => {
                                    if (v.getDate() > 28) {
                                        return false;
                                    }
                                    return true;
                                },
                            }}
                            label={data?.RegularBuyId ? "Next pay date" : "First collection date"}
                            selected={
                                data?.RegularBuyId && this.props.processType != processTypeEnum.Quote
                                    ? JsonDateParser.toDateNoOffset(data?.NextPaymentDate)
                                    : JsonDateParser.toDateNoOffset(data?.FirstCollectionDate)
                            }
                            atLeast={{
                                errorMessage:
                                    "Date of first regular payment must be at least 10 working days from today",
                                value: getFirstPaymentDate(this.props.readonly),
                            }}
                            onChange={(v) => this.handleDateChange(v)}
                            alwaysValidateOnUpdate={false}
                            {...datePickerOptions}
                            required="Please select first collection date"
                        />
                    </div>
                    <div className="col-md-4">
                        {(this.props.subAccountType == SubAccountType.InvestmentAccount ||
                            this.props.subAccountType == SubAccountType.StocksIsa ||
                            this.props.subAccountType == SubAccountType.WrapCash ||
                            SubAccountTypeHelper.isOnPlatformPension(this.props.subAccountType)) && (
                            <SelectEditor
                                options={[true, false]}
                                getText={(v) => {
                                    return v ? "Until date" : "Until notified";
                                }}
                                required="Please select payment frequency"
                                selected={this.state.continueUntilDate}
                                label="Continue"
                                onChange={(v) => {
                                    this.setState({ continueUntilDate: v });
                                    onChange({ ContinueUntil: null });
                                }}
                            />
                        )}
                    </div>
                    <div className="col-md-4">
                        {this.state.continueUntilDate && data?.FirstCollectionDate && (
                            <DatePicker
                                filterDate={{
                                    errorMessage:
                                        "Cannot select first collection/continue until date that is in the past",
                                    validator: (v) => {
                                        return v > new Date();
                                    },
                                }}
                                selected={JsonDateParser.toDate(data?.ContinueUntil)}
                                label="Last collection date"
                                moreThan={{
                                    errorMessage: "Until date must be later than first collection date",
                                    value: JsonDateParser.toDate(data?.FirstCollectionDate),
                                }}
                                onChange={(v) =>
                                    this.onEdit({
                                        ContinueUntil: JsonDateParser.toJsonDateNoOffset(v),
                                    })
                                }
                                alwaysValidateOnUpdate={false}
                                {...datePickerOptions}
                                required="Please select until date"
                            />
                        )}
                    </div>
                </div>
                {this.props.hasPaymentMethod && (
                    <div className="mb-20">
                        <RegularPaymentMethods
                            data={data}
                            isReview={this.props.isReview}
                            onChange={(v) => this.onEdit(v)}
                            bankAccountInstructions={bankAccountInstructions}
                            bankAccounts={bankAccounts}
                            payeeType={this.props.type}
                            subAccountType={this.props.subAccountType}
                            onRef={(ref) => this.appendChild(ref)}
                            showErrorMsgPopUp={this.props.showErrorMsgPopUp}
                            onSaveNewBankAccountInstruction={onSaveNewBankAccountInstruction}
                            onRemoveBankAccountInstruction={onRemoveBankAccountInstruction}
                            inUseBankAccountInstruction={inUseBankAccountInstruction}
                            customerId={clientInfo?.ActorId}
                            autoLineCompleted={this.props.autoLineCompleted}
                        ></RegularPaymentMethods>
                    </div>
                )}

                <div className="row">
                    {SubAccountTypeHelper.isOnPlatformPension(this.props.subAccountType) &&
                        this.props.type !== PaymentType.Employer && (
                            <div className="col-12">
                                <RadiosEditor
                                    cssWrapper="rg-150 mb-8"
                                    label={"Include tax relief?"}
                                    selected={data?.TaxRelief}
                                    options={[true, false]}
                                    getText={(v) => (v ? "Yes" : "No")}
                                    onChange={(v) => this.onEdit({ TaxRelief: v })}
                                    required={"Please select include tax relief?"}
                                    disabled={!this.state.isTaxReliefApplicable}
                                    infoText={
                                        data?.TaxRelief
                                            ? `<strong> ${MoneyDisplayHelper.FromatMoneyDisplay(
                                                  (data?.Money?.Amount || 0) * 1.25,
                                                  data?.Money?.Currency || CurrencyCode.GBP
                                              )} </strong> including tax relief (gross)`
                                            : null
                                    }
                                    infoTextGridClasses="pt-4"
                                />
                            </div>
                        )}
                </div>

                {SubAccountTypeHelper.isOnPlatformPension(this.props.subAccountType) &&
                        this.props.type !== PaymentType.Employer && !data?.TaxRelief &&
                        processType != processTypeEnum.Quote && (
                        <Alert
                            alertType={AlertType.Warning}
                            message="Deselecting tax relief from this payment will mean basic rate tax relief will not be added to the pension contribution2."
                        />
                    )}

                {SubAccountTypeHelper.isOnPlatformPension(this.props.subAccountType) && (
                    <Indexation
                        data={data?.Indexation}
                        firstPaymentDate={JsonDateParser.toDate(data?.FirstCollectionDate)}
                        onChange={(changes) => onChange({ Indexation: changes })}
                        onRef={(ref) => this.appendChild(ref)}
                    />
                )}
                {type != PaymentType.Client && processType != processTypeEnum.Quote && (
                    <ThirdPartyEmployerDetail
                        paymentType={this.props.type}
                        subAccountType={this.props.subAccountType}
                        data={data}
                        onChange={(v) => onChange(v)}
                        onRef={(ref) => this.appendChild(ref)}
                    />
                )}
            </>
        );
    }
}
